import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  HourglassOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  ViewAllButton,
  CloseButton,
  StyledCollapse,
} from './styles';
import { DASHBOARD_ROUTES, BUTTON_LABELS, SECTION_TITLES } from './constants';
import { DailyOperationsSectionProps } from './types';
import { useTheme } from '../../../context/ThemeContext';

const { Panel } = StyledCollapse;

const DailyOperationsSection: React.FC<DailyOperationsSectionProps> = ({
  isOpen,
  onClose,
  isLoading = false,
  dailyOperations,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const stats = [
    {
      label: 'Total Orders',
      value: dailyOperations?.totalOrders ?? 0,
      icon: <ThunderboltOutlined />,
      color: '#4299E1',
    },
    {
      label: 'Completed',
      value: dailyOperations?.completedOrders ?? 0,
      icon: <CheckCircleOutlined />,
      color: '#48BB78',
    },
    {
      label: 'Pending',
      value: dailyOperations?.pendingOrders ?? 0,
      icon: <HourglassOutlined />,
      color: dailyOperations && dailyOperations.pendingOrders > 5 ? '#F56565' : '#ED8936',
    },
    {
      label: 'Avg. Time (min)',
      value: dailyOperations?.averageOrderTime ?? 0,
      icon: <ClockCircleOutlined />,
      color: '#9F7AEA',
    },
  ];

  const cardBg = (color: string) =>
    isDarkMode ? `${color}20` : `${color}15`;

  return (
    <StyledCollapse
      activeKey={isOpen ? ['dailyOps'] : []}
      ghost
      expandIcon={() => null}
    >
      <Panel key="dailyOps" header={null}>
        <SectionCard>
          <SectionHeader>
            <SectionTitle level={4}>{SECTION_TITLES.DAILY_OPERATIONS}</SectionTitle>
            <CloseButton
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              aria-label="Close"
            />
          </SectionHeader>

          {/* 4-stat grid */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {stats.map((stat) => (
              <Col xs={12} sm={6} key={stat.label}>
                <div
                  style={{
                    textAlign: 'center',
                    padding: '16px 8px',
                    borderRadius: 8,
                    background: cardBg(stat.color),
                    border: `1px solid ${stat.color}30`,
                  }}
                >
                  <div style={{ color: stat.color, fontSize: 20, marginBottom: 6 }}>
                    {stat.icon}
                  </div>
                  <div style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: stat.color,
                    lineHeight: 1.2,
                  }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: 11,
                    color: isDarkMode ? '#a0aec0' : '#718096',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginTop: 4,
                  }}>
                    {stat.label}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Completion rate progress bar */}
          {dailyOperations && dailyOperations.totalOrders > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: isDarkMode ? '#a0aec0' : '#718096',
                marginBottom: 6,
              }}>
                <span>Completion Rate</span>
                <span>
                  {Math.round(
                    (dailyOperations.completedOrders / dailyOperations.totalOrders) * 100
                  )}%
                </span>
              </div>
              <div style={{
                height: 8,
                borderRadius: 4,
                background: isDarkMode ? '#4a5568' : '#e2e8f0',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  borderRadius: 4,
                  background: '#48BB78',
                  width: `${Math.round(
                    (dailyOperations.completedOrders / dailyOperations.totalOrders) * 100
                  )}%`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ViewAllButton
              type="primary"
              onClick={() => navigate(DASHBOARD_ROUTES.ORDERS)}
              disabled={isLoading}
            >
              {BUTTON_LABELS.VIEW_ORDERS}
            </ViewAllButton>
          </div>
        </SectionCard>
      </Panel>
    </StyledCollapse>
  );
};

export default DailyOperationsSection;
